import { Typography, Grid, Container } from '@achieve/ascend'
import { ScrollToTopButton } from 'components/Footer/UniversalFooter/ScrollToTopButton/ScrollToTopButton'
import SocialIcons from 'components/Footer/UniversalFooter/SocialIcons/SocialIcons'
import { getSlug } from 'utils/conversions'
import { LayoutContext } from 'providers/LayoutProvider'
import { useContext } from 'react'
import classNames from 'classnames'
import { AchieveLink } from 'components/AchieveLink'

const Footer = ({
  footerNavigation,
  pageDisclosure,
  logoImage,
  logoImageDefault,
  brandCopy,
  trackingCB,
  copyrightMessage,
  showNav = true,
  showFooterSocialIcons = true,
  logoLink = true,
  showMarkers = true,
  markersSection = [],
  mainName,
  schema = {},
  className,
}) => {
  const currentYear = new Date().getFullYear()
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const isAchieve = siteEnv === 'achieve'
  const isFdr = siteEnv === 'website-fdr'
  const isWfs = siteEnv === 'website-wfs'
  const logoWrapperClasses = classNames(
    'relative block',
    isAchieve && 'w-[66px] h-[54px] overflow-hidden lg:w-[66px]',
    isFdr && 'w-[110px] h-[60px]'
  )
  const logoImageClasses = classNames(isAchieve && 'w-[215px] min-w-0 max-w-none')
  return (
    <footer
      className={classNames(
        'bg-blue-50 text-blue-800 [&_a]:text-blue-800',
        !showNav ? 'pt-0' : 'pt-8 lg:pt-16',
        className
      )}
      data-show-nav={showNav ? 'true' : 'false'}
      data-testid="footer"
      data-site={siteEnv}
    >
      <Container
        maxWidth={false}
        disableGutters
        className={`max-w-[1440px] px-16 md:px-8 ${
          isWfs ? 'max-w-[700px]' : 'data-[narrow=true]:max-w-[1280px]'
        } `}
        data-narrow="true"
      >
        {showNav && (
          <div className="flex flex-col pb-8 border-t-0 border-solid border-x-0 border-b border-neutral-150 lg:flex-row lg:justify-between lg:mt-4">
            {footerNavigation.map(({ menuLabel, menuItems = [] }, menuIndex) => {
              return (
                <nav
                  className={classNames(
                    'flex-1 mt-8 lg:mt-0 lg:ml-4 [&_h4]:text-neutral-300',
                    menuIndex === 0 && 'max-w-none lg:max-w-[30%]'
                  )}
                  key={menuIndex}
                  data-testid="footer-menus"
                >
                  <Typography
                    component="h3"
                    fontWeight="bold"
                    variant="labelSm"
                    data-testid="footer-menu-title"
                  >
                    {menuLabel}
                  </Typography>
                  <ul className="p-0 m-0 list-none">
                    {menuItems.map(({ label, testid, url, target = '_self' }, menuItemIndex) => (
                      <li key={menuItemIndex} data-testid={testid} className="mt-2">
                        <AchieveLink
                          data-testid={`footer-menu-${menuItemIndex}`}
                          href={url}
                          target={target}
                          withNextLink
                          track={{
                            nav_link_section: 'Footer Click',
                            click_type: 'Link Click',
                            click_id: label,
                            track_event: 'global_footer',
                            event_type: 'link_click',
                          }}
                          className="text-neutral-975  no-underline hover:underline focus:underline"
                        >
                          <Typography variant="labelSm" fontWeight="regular">
                            {label}
                          </Typography>
                        </AchieveLink>
                      </li>
                    ))}
                  </ul>
                </nav>
              )
            })}
            <div
              className="flex-[2] pt-8 mt-8 lg:mt-0 border-t border-solid border-x-0 border-b-0 border-neutral-150 lg:pt-0 lg:border-t-0 lg:max-w-[336px] lg:ml-4"
              data-testid="footer-menus"
            >
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className="mb-4 flex-initial lg:flex-row-reverse"
              >
                <Grid item>
                  {logoLink ? (
                    // eslint-disable-next-line @next/next/no-html-link-for-pages
                    <AchieveLink
                      className={logoWrapperClasses}
                      href="/"
                      withNextLink
                      track={{
                        nav_link_section: 'Footer',
                        click_type: 'Link Click',
                        click_id: logoImage,
                        track_event: 'global_footer',
                        event_type: 'link_click',
                      }}
                      data-testid={`${getSlug(mainName)}-logo`}
                      aria-label={mainName}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={logoImage ? logoImage : logoImageDefault}
                        alt={`${mainName} Logomark`}
                        loading="lazy"
                        className={logoImageClasses}
                      />
                    </AchieveLink>
                  ) : (
                    <div className={logoWrapperClasses}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={logoImageDefault} alt={`${mainName} Logomark`} loading="lazy" />
                    </div>
                  )}
                </Grid>
                <Grid item>
                  <ScrollToTopButton
                    data-testid="scroll-to-top-button"
                    className="block lg:hidden"
                  />
                </Grid>
              </Grid>
              <Typography
                component="p"
                variant="bodyXs"
                fontWeight="regular"
                className="text-left lg:text-right text-blue-800"
              >
                {brandCopy ||
                  `Achieve is the leader in digital personal finance, built to help everyday people move forward on the path to a better financial future.`}
              </Typography>
              {showFooterSocialIcons && (
                <Grid item className="mt-4 md:mb-0">
                  <SocialIcons
                    items={schema?.oganizationSocialMediaLinks}
                    trackingCB={trackingCB}
                  />
                </Grid>
              )}
              {markersSection?.length > 0 && showMarkers && (
                <Grid container gap={3} alignItems="center" className="mt-4 lg:justify-end">
                  {markersSection.map((item, index) => (
                    <Grid
                      item
                      key={`marker-footer-${index}`}
                      position="relative"
                      className="max-w-[80px] overflow-hidden"
                    >
                      {item}
                    </Grid>
                  ))}
                </Grid>
              )}
            </div>
          </div>
        )}

        {pageDisclosure?.map((dataDisclosure, index) => {
          if (!dataDisclosure) return
          return (
            <div
              key={'dislosure-' + index}
              className="text-blue-800 mt-12 [&_p]:leading-[1.5] [&_p:not(:last-child)]:mb-4 [&_ol]:pl-4 [&_ol>li]:mb-6 [&_li::marker]:text-[11px]"
              data-testid="footer-page-disclosure"
            >
              {dataDisclosure}
            </div>
          )
        })}
        <div className="flex flex-col-reverse lg:flex-row justify-between mt-8 py-8">
          <Grid
            container
            justifyContent="space-between"
            className="flex-col-reverse items-end lg:flex-row lg:items-center"
          >
            <Grid item>
              <Typography
                variant="bodyXs"
                fontWeight="regular"
                className="text-blue-800"
                data-testid="footer-copyright"
              >
                &copy; {currentYear} {copyrightMessage}
              </Typography>
            </Grid>
          </Grid>
        </div>
      </Container>
    </footer>
  )
}

export { Footer }
